import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { ENV } from "@/env";
import type { User } from "@supabase/supabase-js";
import { isAdminUser } from "@/services/adminRoleService";

const SUPER_ADMIN_EMAILS = ["danielcapelani@gmail.com"];
const SUPER_ADMIN_DOMAINS = ["danielcapelano.com.br"];
const ELEVATED_ROLES = new Set(["super_admin", "admin"]);

function isSuperAdmin(currentUser: User | null) {
  if (!currentUser?.email) {
    return false;
  }

  const email = currentUser.email.toLowerCase();
  if (SUPER_ADMIN_EMAILS.includes(email)) {
    return true;
  }

  const domain = email.split("@")[1];
  return domain ? SUPER_ADMIN_DOMAINS.includes(domain) : false;
}

const AUTH_DISABLED = ENV.ADMIN_AUTH_DISABLED;

export function useAdminAuth() {
  const [user, setUser] = useState<User | null>(null);
  const [isAdmin, setIsAdmin] = useState(AUTH_DISABLED);
  const [loading, setLoading] = useState(!AUTH_DISABLED);
  const navigate = useNavigate();

  const appOrigin = useMemo(() => {
    const { location } = globalThis as { location?: Location };
    return location?.origin ?? "";
  }, []);

  const checkAdminAccess = useCallback(async (currentUser: User) => {
    if (AUTH_DISABLED) {
      setIsAdmin(true);
      setLoading(false);
      return;
    }

    try {
      if (isSuperAdmin(currentUser)) {
        setIsAdmin(true);
        setLoading(false);
        return;
      }

      const hasElevatedRole = await isAdminUser(currentUser.id);

      setIsAdmin(hasElevatedRole);
    } catch (error) {
      console.error("Error checking admin role:", error);
      setIsAdmin(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (AUTH_DISABLED) {
      setIsAdmin(true);
      setLoading(false);
      return;
    }

    let isMounted = true;

    const initializeAuth = async () => {
      try {
        const {
          data: { session },
        } = await supabase.auth.getSession();

        if (!session?.user) {
          if (isMounted) {
            setIsAdmin(AUTH_DISABLED);
            setLoading(false);
          }
          return;
        }

        if (isMounted) {
          setUser(session.user);
          await checkAdminAccess(session.user);
        }
      } catch (error) {
        console.error("Error checking auth:", error);
        if (isMounted) {
          setIsAdmin(AUTH_DISABLED);
          setLoading(false);
        }
      }
    };

    void initializeAuth();

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(async (_event, session) => {
      if (session?.user) {
        setUser(session.user);
        await checkAdminAccess(session.user);
      } else {
        setUser(null);
        setIsAdmin(AUTH_DISABLED);
        setLoading(false);
      }
    });

    return () => {
      isMounted = false;
      subscription.unsubscribe();
    };
  }, [checkAdminAccess]);

  async function signInWithGoogle() {
    try {
      const redirectUrl = `${appOrigin}/admin`;

      const { error } = await supabase.auth.signInWithOAuth({
        provider: "google",
        options: {
          redirectTo: redirectUrl,
        },
      });

      if (error) throw error;
    } catch (error: any) {
      console.error("Error signing in with Google:", error);
      throw new Error(error.message || "Erro ao fazer login com Google");
    }
  }

  async function signInWithPassword(email: string, password: string) {
    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) throw error;

      if (data.user) {
        setUser(data.user);
        await checkAdminAccess(data.user);
      }

      return data.user;
    } catch (error: any) {
      console.error("Error signing in with password:", error);
      throw new Error(error.message || "Erro ao fazer login");
    }
  }

  async function signUpWithPassword(email: string, password: string, fullName?: string) {
    try {
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: fullName ? { full_name: fullName } : undefined,
          emailRedirectTo: `${appOrigin}/admin-login`,
        },
      });

      if (error) throw error;

      return data.user;
    } catch (error: any) {
      console.error("Error signing up:", error);
      throw new Error(error.message || "Erro ao criar conta");
    }
  }

  async function sendPasswordReset(email: string) {
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${appOrigin}/admin-reset-password`,
      });

      if (error) throw error;
    } catch (error: any) {
      console.error("Error sending password reset:", error);
      throw new Error(error.message || "Erro ao enviar recuperação de senha");
    }
  }

  async function signOut() {
    try {
      const { error } = await supabase.auth.signOut();
      if (error) throw error;
      
      setUser(null);
      setIsAdmin(AUTH_DISABLED);
      navigate(AUTH_DISABLED ? "/admin" : "/admin-login");
    } catch (error: any) {
      console.error("Error signing out:", error);
      throw new Error(error.message || "Erro ao fazer logout");
    }
  }

  return {
    user,
    isAdmin,
    loading,
    signInWithGoogle,
    signInWithPassword,
    signUpWithPassword,
    sendPasswordReset,
    signOut,
  };
}
